// src/components/YouTubeVideoDisplay.js
import React, { useRef, useEffect } from "react";
import YouTube from "react-youtube";

const YT_ENDED = 0;

function idFromUrl(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) {
      return parsed.pathname.replace("/", "");
    }
    if (parsed.hostname.includes("youtube.com")) {
      if (parsed.pathname.includes("/embed/")) {
        return parsed.pathname.split("/embed/")[1]?.split("/")[0] || "";
      }
      return parsed.searchParams.get("v") || "";
    }
  } catch (_) {
    return "";
  }
  return "";
}

function getProvider(url) {
  if (!url || typeof url !== "string") return "unknown";
  try {
    const host = new URL(url).hostname;
    if (host.includes("youtube.com") || host.includes("youtu.be")) return "youtube";
    if (host.includes("rutube.ru")) return "rutube";
  } catch (_) {
    return "unknown";
  }
  return "unknown";
}

function toRutubeEmbedUrl(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("rutube.ru")) return "";
    if (parsed.pathname.includes("/play/embed/")) return url;
    const match = parsed.pathname.match(/\/video\/([a-zA-Z0-9]+)/);
    if (match?.[1]) {
      return `https://rutube.ru/play/embed/${match[1]}`;
    }
  } catch (_) {
    return "";
  }
  return "";
}

function rutubeIdFromUrl(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("rutube.ru")) return "";
    if (parsed.pathname.includes("/play/embed/")) {
      return parsed.pathname.split("/play/embed/")[1]?.split("/")[0] || "";
    }
    const match = parsed.pathname.match(/\/video\/([a-zA-Z0-9]+)/);
    return match?.[1] || "";
  } catch (_) {
    return "";
  }
}

/** Match YouTube's autoplay=1 behavior: RuTube uses embed query `autoplay=true`. */
function withRutubeAutoplay(embedUrl) {
  if (!embedUrl) return "";
  try {
    const u = new URL(embedUrl);
    u.searchParams.set("autoplay", "true");
    return u.toString();
  } catch (_) {
    return embedUrl;
  }
}

function toIframeUrl(url) {
  const provider = getProvider(url);
  if (provider === "youtube") {
    const id = idFromUrl(url);
    return id ? `https://www.youtube.com/embed/${id}` : "";
  }
  if (provider === "rutube") {
    return withRutubeAutoplay(toRutubeEmbedUrl(url));
  }
  return url;
}

function parseRutubeMessageData(rawData) {
  if (!rawData) return null;
  if (typeof rawData === "string") {
    try {
      return JSON.parse(rawData);
    } catch (_) {
      return null;
    }
  }
  if (typeof rawData === "object") return rawData;
  return null;
}

export default function YouTubeVideoDisplay({ videoUrl, videoUrls, currentIndex, onEnded }) {
  // Support legacy single-video usage: only videoUrl and onEnded
  const isPlaylist = Array.isArray(videoUrls) && videoUrls.length > 0;
  const urls = isPlaylist ? videoUrls : (videoUrl ? [videoUrl] : []);
  const index = isPlaylist ? currentIndex : 0;
  const ids = urls.map(idFromUrl);
  const rutubeIds = urls.map(rutubeIdFromUrl);
  const providers = urls.map(getProvider);
  const supportsYoutubeApiPlaylist = providers.length > 0 && providers.every((p) => p === "youtube");
  const supportsRutubeApiPlaylist =
    providers.length > 0 &&
    providers.every((p) => p === "rutube") &&
    rutubeIds.every(Boolean);
  const stableId = ids[0]; // always use first so the iframe never resets (keeps fullscreen)
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const onEndedFiredRef = useRef(false);
  const lastLoadedIndexRef = useRef(null);
  const lastRutubeLoadedIndexRef = useRef(null);
  const rutubeDurationRef = useRef(0);
  const rutubeTimeRef = useRef(0);
  const rutubeChangedAtRef = useRef(0);
  const rutubeWasFullscreenRef = useRef(false);

  const canUsePlayerApi = (player) => {
    if (!player || typeof player.loadVideoById !== "function") return false;
    try {
      const iframe = typeof player.getIframe === "function" ? player.getIframe() : null;
      return iframe != null;
    } catch (_) {
      return false;
    }
  };

  const opts = {
    width: "100%",
    height: "100%",
    playerVars: {
      autoplay: 1,
      controls: 1,
      rel: 0,
      modestbranding: 1,
      iv_load_policy: 3,
      enablejsapi: 1,
    },
  };

  const _onReady = (event) => {
    playerRef.current = event.target;
    onEndedFiredRef.current = false;
    if (ids.length === 0) return;
    if (index > 0 && ids[index] && canUsePlayerApi(event.target)) {
      event.target.loadVideoById(ids[index]);
      lastLoadedIndexRef.current = index;
    } else {
      lastLoadedIndexRef.current = 0;
    }
  };

  const _onStateChange = (event) => {
    if (event.data === YT_ENDED || (typeof window !== "undefined" && window.YT && event.data === window.YT.PlayerState.ENDED)) {
      if (!onEndedFiredRef.current) {
        onEndedFiredRef.current = true;
        if (isPlaylist && index < ids.length - 1 && playerRef.current) {
          const nextId = ids[index + 1];
          playerRef.current.loadVideoById(nextId);
          lastLoadedIndexRef.current = index + 1;
        }
        onEnded();
      }
    }
  };

  // Poll player state as fallback when onStateChange doesn't fire (e.g. in fullscreen)
  useEffect(() => {
    if (!supportsYoutubeApiPlaylist) return;
    if (!playerRef.current || !onEnded || ids.length === 0) return;
    onEndedFiredRef.current = false;

    const interval = setInterval(() => {
      try {
        const player = playerRef.current;
        if (!player || !player.getPlayerState) return;
        const state = player.getPlayerState();
        const ended = state === YT_ENDED || (typeof window !== "undefined" && window.YT && state === window.YT.PlayerState.ENDED);
        if (ended && !onEndedFiredRef.current) {
          onEndedFiredRef.current = true;
          if (isPlaylist && index < ids.length - 1) {
            const nextId = ids[index + 1];
            player.loadVideoById(nextId);
            lastLoadedIndexRef.current = index + 1;
          }
          onEnded();
        }
      } catch (_) {}
    }, 1000);

    return () => clearInterval(interval);
  }, [ids.join(","), index, isPlaylist, onEnded, supportsYoutubeApiPlaylist]);

  // When currentIndex changes from outside (e.g. user clicked Previous), load that video in same player
  useEffect(() => {
    if (!supportsYoutubeApiPlaylist) return;
    const player = playerRef.current;
    if (!canUsePlayerApi(player) || ids.length === 0 || !ids[index]) return;
    if (lastLoadedIndexRef.current === index) return;
    try {
      player.loadVideoById(ids[index]);
      lastLoadedIndexRef.current = index;
    } catch (_) {
      playerRef.current = null;
    }
  }, [index, ids.join(","), supportsYoutubeApiPlaylist]);

  useEffect(() => {
    if (!supportsYoutubeApiPlaylist) {
      playerRef.current = null;
      lastLoadedIndexRef.current = null;
    }
  }, [supportsYoutubeApiPlaylist]);

  useEffect(() => {
    if (!supportsRutubeApiPlaylist) {
      lastRutubeLoadedIndexRef.current = null;
      return;
    }
    if (!iframeRef.current || !iframeRef.current.contentWindow) return;
    if (!rutubeIds[index]) return;
    if (lastRutubeLoadedIndexRef.current === index) return;

    const payload = JSON.stringify({
      type: "player:changeVideo",
      data: { id: rutubeIds[index] },
    });

    let attempts = 0;
    const maxAttempts = 8;
    const trySend = () => {
      attempts += 1;
      try {
        iframeRef.current?.contentWindow?.postMessage(payload, "https://rutube.ru");
      } catch (_) {}
      if (attempts < maxAttempts) {
        setTimeout(trySend, 250);
      }
    };

    trySend();
    lastRutubeLoadedIndexRef.current = index;
  }, [index, rutubeIds.join(","), supportsRutubeApiPlaylist]);

  useEffect(() => {
    if (!supportsRutubeApiPlaylist) return;
    rutubeDurationRef.current = 0;
    rutubeTimeRef.current = 0;
    onEndedFiredRef.current = false;
  }, [supportsRutubeApiPlaylist, index]);

  // Best-effort: if RuTube exits fullscreen during our changeVideo transition,
  // immediately re-request fullscreen on the iframe so we stay in fullscreen.
  useEffect(() => {
    if (!supportsRutubeApiPlaylist) return undefined;
    const handler = () => {
      const inFullscreen = !!(
        document.fullscreenElement || document.webkitFullscreenElement
      );
      if (inFullscreen) return;
      const recently = Date.now() - rutubeChangedAtRef.current < 3000;
      if (!recently || !rutubeWasFullscreenRef.current) return;
      rutubeWasFullscreenRef.current = false;
      const elem = iframeRef.current;
      if (!elem) return;
      const req =
        elem.requestFullscreen?.bind(elem) ||
        elem.webkitRequestFullscreen?.bind(elem) ||
        elem.webkitEnterFullscreen?.bind(elem) ||
        elem.mozRequestFullScreen?.bind(elem) ||
        elem.msRequestFullscreen?.bind(elem);
      if (req) {
        try {
          const result = req();
          if (result && typeof result.catch === "function") {
            result.catch(() => {});
          }
        } catch (_) {}
      }
    };
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
    };
  }, [supportsRutubeApiPlaylist]);

  const currentUrl = urls[index];
  const currentProvider = getProvider(currentUrl);
  const iframeUrl = supportsRutubeApiPlaylist
    ? withRutubeAutoplay(`https://rutube.ru/play/embed/${rutubeIds[0]}`)
    : toIframeUrl(currentUrl);

  useEffect(() => {
    onEndedFiredRef.current = false;
  }, [index, iframeUrl]);

  // RuTube playback completion events come via postMessage from the embed iframe.
  useEffect(() => {
    if (supportsYoutubeApiPlaylist) return undefined;
    if (currentProvider !== "rutube") return undefined;
    if (!onEnded) return undefined;

    const onMessage = (event) => {
      if (!event?.origin || !event.origin.includes("rutube.ru")) return;
      const message = parseRutubeMessageData(event.data);
      if (!message || typeof message !== "object") return;

      // Pre-emptively switch BEFORE the current video reaches the end-screen.
      // RuTube exits fullscreen when the player transitions into end-screen on `playComplete`,
      // so we trigger the next video while the current one is still playing.
      if (supportsRutubeApiPlaylist) {
        if (
          message.type === "player:durationChange" &&
          typeof message?.data?.duration === "number"
        ) {
          rutubeDurationRef.current = message.data.duration;
        }
        if (
          message.type === "player:currentTime" &&
          typeof message?.data?.time === "number"
        ) {
          rutubeTimeRef.current = message.data.time;
          const duration = rutubeDurationRef.current;
          const time = rutubeTimeRef.current;
          const NEAR_END_SECONDS = 0.7;
          const hasNext = index < rutubeIds.length - 1;
          if (
            !onEndedFiredRef.current &&
            hasNext &&
            duration > 1 &&
            time > 0 &&
            time >= duration - NEAR_END_SECONDS
          ) {
            onEndedFiredRef.current = true;
            const nextId = rutubeIds[index + 1];
            rutubeWasFullscreenRef.current = !!(
              document.fullscreenElement || document.webkitFullscreenElement
            );
            rutubeChangedAtRef.current = Date.now();
            try {
              const payload = JSON.stringify({
                type: "player:changeVideo",
                data: { id: nextId },
              });
              iframeRef.current?.contentWindow?.postMessage(payload, "https://rutube.ru");
              setTimeout(() => {
                try {
                  iframeRef.current?.contentWindow?.postMessage(
                    JSON.stringify({ type: "player:play", data: {} }),
                    "https://rutube.ru"
                  );
                } catch (_) {}
              }, 200);
            } catch (_) {}
            lastRutubeLoadedIndexRef.current = index + 1;
            onEnded();
            return;
          }
        }
      }

      const isCompleteEvent =
        message.type === "player:playComplete" ||
        (message.type === "player:changeState" && message?.data?.state === "completed");

      if (!isCompleteEvent || onEndedFiredRef.current) return;
      onEndedFiredRef.current = true;
      onEnded();
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [
    supportsYoutubeApiPlaylist,
    supportsRutubeApiPlaylist,
    currentProvider,
    onEnded,
    index,
    rutubeIds.join(","),
  ]);

  if (supportsYoutubeApiPlaylist && !stableId) return null;

  return (
    <div className="relative w-full pb-[56.25%]">
      {supportsYoutubeApiPlaylist ? (
        <YouTube
          videoId={stableId}
          opts={opts}
          onReady={_onReady}
          onStateChange={_onStateChange}
          className="absolute top-0 left-0 w-full h-full"
        />
      ) : (
        <iframe
          title="Embedded Video"
          src={iframeUrl}
          ref={iframeRef}
          className="absolute top-0 left-0 w-full h-full rounded-md"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
          allowFullScreen
        />
      )}
    </div>
  );
}
